import { useEffect, useRef, useState } from "react"
import type { CSSProperties } from "react"
import { DesktopNav } from "./DesktopNav"
import { Logo } from "./Logo"
import { MobileActions } from "./MobileActions"

const WHITE_RGB: [number, number, number] = [255, 255, 255]
const MUTED_RGB: [number, number, number] = [244, 244, 245]
const MUTED_GRADIENT_TAIL = `rgba(${MUTED_RGB.join(", ")}, 0) 100%`
const HERO_MODE_OFFSET = 100; /168//184//240
const HERO_MODE_DEADZONE = 16

type SiteHeaderProps = {
  gradientInterpolant?: number
  showMobileClose?: boolean
  onMobileClose?: () => void
  className?: string
}

const clamp = (value: number) => Math.min(1, Math.max(0, value))
const mixToMuted = (progress: number) => {
  const boundedProgress = clamp(progress)
  return `rgb(${WHITE_RGB.map((start, index) => Math.round(start + (MUTED_RGB[index] - start) * boundedProgress)).join(", ")})`
}

export const SiteHeader = ({
  gradientInterpolant = 1,
  showMobileClose = false,
  onMobileClose,
  className,
}: SiteHeaderProps) => {
  const [logoMode, setLogoMode] = useState<"logo" | "title">("logo")
  const modeRef = useRef(logoMode)
  useEffect(() => {
    if (typeof window === "undefined") return
    const node = document.querySelector<HTMLElement>("[data-hero-section]")
    if (!node) return
    let raf = 0
    const syncMode = () => {
      const rect = node.getBoundingClientRect()
      const threshold = -rect.height + HERO_MODE_OFFSET
      const revert = threshold + HERO_MODE_DEADZONE
      const next = rect.top <= threshold ? "title" : rect.top >= revert ? "logo" : modeRef.current
      if (next !== modeRef.current) {
        modeRef.current = next
        setLogoMode(next)
      }
    }
    const schedule = () => {
      if (raf) return
      raf = window.requestAnimationFrame(() => {
        raf = 0
        syncMode()
      })
    }
    syncMode()
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule)
    return () => {
      if (raf) window.cancelAnimationFrame(raf)
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
    }
  }, [])
  const gradientBaseColor = mixToMuted(gradientInterpolant)
  const grad = `${gradientBaseColor} 0%, ${gradientBaseColor} 35%, ${MUTED_GRADIENT_TAIL}`
  return (
    <header
      className={`sticky top-0 bg-gradient-to-b z-1 overflow-hidden${className ? ` ${className}` : ""}`}
      style={{ "--tw-gradient-stops": grad } as CSSProperties}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          <Logo mode={logoMode} />
          <DesktopNav />
          <MobileActions showClose={showMobileClose} onClose={onMobileClose} />
        </div>
      </div>
    </header>
  )
}
